import {
    Box,
    Chip,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router";

import { ResourceDetail } from "@rossum/upcloud-costs-shared";

import { fetchResource } from "../../api/resources-api";
import { AsyncContent } from "../../common/AsyncContent";
import { formatMoney, formatMonth } from "../../common/format";
import { useAsync } from "../../common/use-async";
import { AttributionValue } from "./AttributionValue";

/** A resource's identity, labels, parent, and monthly costs with their attribution. */
export function ResourcePage() {
    const { cloudResourceId = "" } = useParams();
    const resource = useAsync(() => fetchResource(cloudResourceId), [cloudResourceId]);
    return <AsyncContent state={resource}>{(detail) => <ResourceView resource={detail} />}</AsyncContent>;
}

function ResourceView({ resource }: { resource: ResourceDetail }) {
    const { t, i18n } = useTranslation();
    const facts: [string, React.ReactNode][] = [
        [t("resource.resourceId"), resource.resourceId],
        [
            t("resource.type"),
            `${t(`category.${resource.category}`, { defaultValue: resource.category })} / ${resource.type}`,
        ],
        [t("resource.zone"), resource.zone ?? "–"],
        [t("resource.hostname"), resource.hostname ?? "–"],
        [t("resource.cluster"), resource.kubernetesCluster ?? "–"],
        [
            t("resource.parent"),
            resource.parent === null ? (
                "–"
            ) : (
                <Link component={RouterLink} to={`/resources/${resource.parent.cloudResourceId}`}>
                    {resource.parent.name ?? resource.parent.resourceId}
                </Link>
            ),
        ],
    ];

    return (
        <Stack spacing={3}>
            <Typography variant="h5" component="h2">
                {resource.name ?? resource.resourceId}
            </Typography>
            <Paper sx={{ p: 2 }}>
                <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: 1, m: 0 }}>
                    {facts.map(([term, description]) => (
                        <Box key={term} sx={{ display: "contents" }}>
                            <Typography component="dt" variant="body2" color="text.secondary">
                                {term}
                            </Typography>
                            <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                                {description}
                            </Typography>
                        </Box>
                    ))}
                    <Typography component="dt" variant="body2" color="text.secondary">
                        {t("resource.labels")}
                    </Typography>
                    <Box component="dd" sx={{ m: 0, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {resource.labels.length === 0
                            ? "–"
                            : resource.labels.map((label) => (
                                  <Chip key={label.key} size="small" label={`${label.key}=${label.value}`} />
                              ))}
                    </Box>
                </Box>
            </Paper>
            <TableContainer component={Paper}>
                <Table size="small" aria-label={t("resource.title")}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("resource.month")}</TableCell>
                            <TableCell align="right">{t("resource.amount")}</TableCell>
                            <TableCell align="right">{t("resource.hours")}</TableCell>
                            <TableCell>{t("resource.plan")}</TableCell>
                            <TableCell>{t("resource.tenant")}</TableCell>
                            <TableCell>{t("resource.service")}</TableCell>
                            <TableCell>{t("resource.environment")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resource.months.map((month) => (
                            <TableRow key={month.month}>
                                <TableCell>{formatMonth(month.month, i18n.language)}</TableCell>
                                <TableCell align="right">{formatMoney(month.amount, null, i18n.language)}</TableCell>
                                <TableCell align="right">{month.hours}</TableCell>
                                <TableCell>
                                    {[
                                        ...new Set(
                                            month.details.map((detail) => detail.plan).filter((plan) => plan !== null),
                                        ),
                                    ].join(", ") || "–"}
                                </TableCell>
                                <TableCell>
                                    <AttributionValue
                                        value={month.attribution.tenant}
                                        source={month.attribution.tenantSource}
                                    />
                                </TableCell>
                                <TableCell>
                                    <AttributionValue
                                        value={month.attribution.service}
                                        source={month.attribution.serviceSource}
                                    />
                                </TableCell>
                                <TableCell>
                                    <AttributionValue
                                        value={
                                            month.attribution.environment === null
                                                ? null
                                                : t(`environment.${month.attribution.environment}`)
                                        }
                                        source={month.attribution.environmentSource}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
}
